<template>
  <nav class="nav" ref="navEl">
    <div class="container">
      <div class="nav-grid">
        <!-- Logo -->
        <RouterLink class="logo" :to="logoUrl">
          <img :src="branding.logo" @click="refresh" />
        </RouterLink>

        <!-- Not Authenticated -->
        <div v-if="!isAuthenticated && !hideLogNowSignin" class="login-nav">
          <RouterLink class="signin-btn" to="/signin" @click="login"
            >Sign In</RouterLink
          >
          <p>/</p>
          <RouterLink class="signin-btn" to="/join" @click="login"
            >Join Now</RouterLink
          >
        </div>

        <!-- Authenticated -->
        <div v-if="isAuthenticated" class="loggedIn">
          <div class="loggedIn-grid">
            <div class="flex link-nav">
              <RouterLink class="signin-btn" to="/collect-home"
                >Collect Points</RouterLink
              >
              <p>|</p>
              <RouterLink class="signin-btn" to="/spend"
                >Spend Points</RouterLink
              >
            </div>

            <div
              class="my-profile-wrapper"
              @mouseenter="showAccountMenu = true"
              @mouseleave="showAccountMenu = false"
            >
              <RouterLink to="/my-account" class="my-profile">
                <img class="profileIcon" :src="profileIcon" />
              </RouterLink>

              <transition name="fade">
                <div v-if="showAccountMenu" class="account-bubble">
                  <div class="account-bubble-inner">
                    <RouterLink to="/statement" @click="showAccountMenu = false"
                      >Account Statement</RouterLink
                    >
                    <RouterLink to="/my-profile" @click="showAccountMenu = false"
                      >My Profile</RouterLink
                    >
                    <RouterLink to="/help-centre" @click="showAccountMenu = false"
                      >Help Centre</RouterLink
                    >
                    <RouterLink
                      to="/contact-preferences"
                      @click="showAccountMenu = false"
                      >Contact Preferences</RouterLink
                    >
                    <a href="#" @click.prevent="onLogout">Log Out</a>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Burger Menu -->
            <div class="burger-menu" @click="toggleMenu">
              <span :class="{ open: isOpen }"></span>
              <span :class="{ open: isOpen }"></span>
              <span :class="{ open: isOpen }"></span>
            </div>
          </div>
        </div>

        <!-- Search Bar -->
        <div v-if="isAuthenticated" class="search-wrapper">
          <searchBar />
        </div>
      </div>
    </div>
  </nav>

  <!-- Animated dropdown -->
  <transition name="slide-fade">
    <ul v-if="isOpen && isAuthenticated" class="menu">
      <li>
        <RouterLink to="/collect-home" @click="closeMenu"
          >Collect Points</RouterLink
        >
      </li>
      <li>
        <RouterLink to="/spend" @click="closeMenu">Spend Points</RouterLink>
      </li>
      <li v-for="item in quicklinks" :key="item.url">
        <a
          v-if="item.url.startsWith('http://') || item.url.startsWith('https://')"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
          @click="closeMenu"
          >{{ item.title }}</a
        >
        <RouterLink v-else :to="item.url" @click="closeMenu">{{
          item.title
        }}</RouterLink>
      </li>
      <li>
        <a href="#" @click.prevent="onLogout">Log Out</a>
      </li>
    </ul>
  </transition>

  <!-- Quick Links -->
  <div class="quick-links" v-if="isAuthenticated">
      <div class="quick-links-container">
          <template v-for="(quicklink, index) in quicklinks" :key="quicklink.url">

              <!-- External links -->
              <a v-if="quicklink.url.startsWith('http://') || quicklink.url.startsWith('https://')"
                 :href="quicklink.url"
                 target="_blank"
                 rel="noopener noreferrer">
                  {{ quicklink.title }}
              </a>

              <!-- Internal links -->
              <RouterLink v-else
                          :to="quicklink.url">
                  {{ quicklink.title }}
              </RouterLink>

              <p v-if="index < quicklinks.length - 1">|</p>

          </template>
      </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useTenantConfig } from "../composables/useTenantConfig";
import TenantService from "../services/tenant-service";
import { Tenants } from "../constants/Tenants";
import { useAuthStore } from "../stores/auth";
import { useMemberAccountBalanceStore } from "../stores/member-account-balance";
import ssoService from "../services/sso-service";
import searchBar from "../components/SearchBar.vue";

export default defineComponent({
  name: "Nav",
  components: {
    searchBar,
  },
  setup() {
    const { branding, content, features, isLoaded, settings } = useTenantConfig();
    const authStore = useAuthStore();
    const memberAccountBalanceStore = useMemberAccountBalanceStore();
    const hideLogNowSignin = ref(false);
    const isOpen = ref(false);
    const showAccountMenu = ref(false);
    const profileIcon: string = require("../assets/images/TEMPDEV/MyProfile.svg");
    const tenant = ref("");
    const router = useRouter();
    const route = useRoute();

    tenant.value = TenantService.getTenantName();

    if (tenant.value === Tenants.Rewards4Wolves) {
      hideLogNowSignin.value = true;
    }

    // Keep the sign in / join now links in sync with navigation (including browser back/forward)
    watch(
      () => route.name,
      (routeName) => {
        if (tenant.value === Tenants.Rewards4Wolves) {
          hideLogNowSignin.value = true;
          return;
        }

        hideLogNowSignin.value = routeName === "signin" || routeName === "join";
      },
      { immediate: true }
    );

    // Make isAuthenticated reactive
    const isAuthenticated = computed(() => authStore.isAuthenticated);

    // ? Define logoUrl *once* as a computed property
    const logoUrl = computed(() => (isAuthenticated.value ? "/home" : "/"));

    const toggleMenu = () => {
      isOpen.value = !isOpen.value;
    };

    const login = () => {
      hideLogNowSignin.value = true;
    };

    // ? This just resets UI state; don�t redefine logoUrl here
    const refresh = () => {
      if (tenant.value === Tenants.Rewards4Wolves) {
        hideLogNowSignin.value = true;
      } else {
        hideLogNowSignin.value = false;
      }
    };

    const closeMenu = () => {
      isOpen.value = false;
    };

    const onLogout = async () => {
      showAccountMenu.value = false;
      const tenantSettings = settings.value?.tenantSettings ?? [];
      const applicationName = String(
        tenantSettings.find((x: any) => x.name === "ApplicationName")?.value ?? ""
      );
      const clubId = Number(
        tenantSettings.find((x: any) => x.name === "ClubId")?.value ?? 0
      );

      if (tenant.value === Tenants.Rewards4Wolves) {
        const ssoLogoutUrl = await ssoService.getSsoLogoutUrl(applicationName, clubId);
        window.location.href = ssoLogoutUrl;
      } else {
        await authStore.signout();
        router.push("/");
      }
    };

    const quicklinks = computed(() => content.value?.nav?.quicklinks ?? []);

    watch(
      () => authStore.isAuthenticated,
      (isAuth) => {
        if (isAuth) {
          memberAccountBalanceStore.refreshAccountBalance();
        }
      },
      { immediate: true }
    );

    const navEl = ref<HTMLElement | null>(null);
    let resizeObserver: ResizeObserver | null = null;

    onMounted(() => {
      if (navEl.value) {
        const updateNavHeight = () => {
          document.documentElement.style.setProperty(
            '--nav-height',
            `${navEl.value!.offsetHeight}px`
          );
        };
        updateNavHeight();
        resizeObserver = new ResizeObserver(updateNavHeight);
        resizeObserver.observe(navEl.value);
      }
    });

    onBeforeUnmount(() => {
      resizeObserver?.disconnect();
    });

    return {
      navEl,
      tenant,
      profileIcon,
      hideLogNowSignin,
      isAuthenticated,
      login,
      refresh,
      toggleMenu,
      isOpen,
      closeMenu,
      showAccountMenu,
      onLogout,
      branding,
      content,
      features,
      isLoaded,
      quicklinks,
      logoUrl,
    };
  },
});
</script>

<style scoped>
.nav {
  position: relative;
  background-color: var(--tenant-primary-color);
  background: linear-gradient(
    var(--tenant-primary-color) 60%,
    var(--tenant-secondary-color-10)
  );
  padding-block: 1rem;
  border-bottom: 4px solid var(--tenant-secondary-color);
  z-index: 10002;
}

.container {
  max-width: min(100% - 30px, 1000px);
  margin-inline: auto;
  align-items: center;
}

.nav-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
}

.login-nav {
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: 0.5rem;
  justify-content: end;
  color: white;
  grid-row: 1 / span 2;
}

.logo {
  /*     display: contents;*/
  grid-row: 1 / span 2;
  align-self: center;
}

.logo img {
  grid-row: 1 / 3;
  max-height: 65px;
  align-self: center;
}

.profileIcon {
  max-height: 25px;
}

.loggedIn {
  align-self: center;
}

.loggedIn-grid {
  display: grid;
  justify-content: end;
  gap: 0.8rem;
  padding-inline: 0.3rem;
  grid-column: 1 / span 2;
}

.btn-flex p {
  color: white;
  padding-inline: 0.3rem;
  font-weight: 600;
  align-self: center;
  font-size: 20px;
}

.loggedIn-grid p {
  color: white;
  padding-inline: 0.3rem;
  font-weight: 600;
  align-self: center;
  font-size: 20px;
}

.btn-flex {
  display: flex;
  padding-right: 15px;
}

.signin-btn {
  text-decoration: none;
  display: inline;
  color: var(--clr-white);
  border-radius: 15px;
  font-size: 18px;
  justify-self: end;
  align-content: center;
  margin-block: 0.3rem;
}

a:hover {
  text-decoration: underline;
}

.my-profile-wrapper {
  position: relative;
  width: fit-content;
  margin-left: auto;
}

.my-profile {
  text-decoration: none;
  display: flex;
  align-items: center;
}

.my-profile:hover {
  text-decoration: none;
}

.account-bubble {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: -6px;
  z-index: 200;
}

.account-bubble::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 16px;
  height: 16px;
  background: var(--clr-white);
  box-shadow: -3px -3px 5px -3px rgb(0 0 0 / 10%);
  z-index: 1;
}

.account-bubble-inner {
  position: absolute;
  top: 8px;
  right: 0;
  background: var(--clr-white);
  border-radius: 6px;
  box-shadow: 0px 6px 8px 0px rgb(0 0 0 / 20%);
  display: flex;
  flex-direction: column;
  min-width: 200px;
}

.account-bubble-inner a {
  position: relative;
  padding: 0.85rem 1.1rem;
  color: #333;
  text-decoration: none;
  font-size: 15px;
  white-space: nowrap;
  background: var(--clr-white);
}

.account-bubble-inner a:not(:last-child) {
  border-bottom: 1px solid #ecf0f3;
}

.account-bubble-inner a:hover {
  background: #f4f4f4;
  text-decoration: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.pofileIcon {
  height: 20px;
  color: aliceblue;
}

.balance {
  color: white;
  height: 18px;
}

.quick-links {
  display: none;
}

.burger-menu {
  display: grid;
  gap: 0.4rem;
  align-self: center;
  height: 20px;
  justify-self: end;
  margin-bottom: 1rem;
}

.link-nav {
  display: none;
}

.burger-menu span {
  width: 32px;
  height: 3px;
  margin: 1px 0;
  border-radius: 50px;
  background-color: var(--clr-white);
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.burger-menu span.open:nth-child(1) {
  transform: translateY(12px) rotate(45deg);
}

.burger-menu span.open:nth-child(2) {
  opacity: 0;
}

.burger-menu span.open:nth-child(3) {
  transform: translateY(-12px) rotate(-45deg);
}

/* Add your CSS styles here to style the navbar */
.menu-items {
  display: flex;
  gap: 2rem;
  align-items: center;
  color: white;
}

.menu-item {
  position: relative;
}

.menu-items a {
  text-decoration: none;
  color: var(--clr-black);
  font-size: 18px;
}

ul {
  list-style-type: none;
  padding: 0;
}

.menu {
  list-style: none;
  background: var(--clr-white);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  left: 0;
  right: 0;
  z-index: 100;
  box-shadow: 0px 6px 8px 0px rgb(0 0 0 / 20%);
}

.search-wrapper {
  grid-column: 1 / span 2;
}

.menu li {
  cursor: pointer;
  text-decoration: none;
  /*      border-bottom: 2px solid white;*/
}

.menu li:not(:first-child) {
  border-top: 2px solid #ecf0f3;
}

.menu li a {
  display: flex;
  color: black;
  text-decoration: none;
  padding: 1rem;
}

/* Dropdown animation */
.slide-fade-enter-active {
  transition: height 0.5s, opacity 1s ease-in-out;
}

.slide-fade-leave-active {
  transition: height 1s, opacity 0.3s ease-in-out;
}

.slide-fade-enter-from {
  height: 0;
  opacity: 0;
}

.slide-fade-enter-to {
  height: 460px;
  opacity: 1;
}

.slide-fade-leave-from {
  height: 460px;
  opacity: 1;
}

.slide-fade-leave-to {
  height: 0;
  opacity: 0;
}

@media (min-width: 700px) {
  .search-wrapper {
    grid-column: 2;
  }

  .account-bubble {
    display: block;
  }

  .quick-links {
    display: block;
    padding-block: 0.1rem;
    background-color: var(--tenant-secondary-color);
    color: white;
    border-bottom: 4px solid var(--tenant-secondary-color);
    box-shadow: 0 7px 15px -7px #333333ab;
  }

  .quick-links-container a {
    color: white;
    text-decoration: none;
  }

  .quick-links-container {
    max-width: min(100% - 30px, 1000px);
    display: flex;
    margin-inline: auto;
    justify-content: space-between;
  }

  .burger-menu,
  .menu {
    display: none;
  }

  .link-nav {
    display: flex;
  }

  .container {
    grid-template-columns: 1fr 1fr;
  }

  .login-nav {
    grid-row: 1 / span 2;
  }

  .loggedIn-grid {
    justify-content: space-between;
    display: flex;
  }

  /*    input {
            grid-column: 2 / 2;
        }*/

  .logo img {
    grid-row: 1 / span 3;
  }

  .loggedIn {
    align-self: end;
  }
}
</style>
